import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

export default function WorkLogCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [pinLast, setPinLast] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/work-log-categories');
      setCategories(res.data?.categories || []);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to load categories.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setPinLast(false);
  };

  const startEdit = (category) => {
    setEditingId(category.categoryId);
    setName(category.name);
    setPinLast(category.pinLast);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await axiosInstance.put(`/admin/work-log-categories/${editingId}`, { name: name.trim(), pinLast });
      } else {
        await axiosInstance.post('/admin/work-log-categories', { name: name.trim(), pinLast });
      }
      resetForm();
      await loadCategories();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save this category.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `Delete "${category.name}"?`,
      text: 'Work log entries already using this category will keep it unlabeled — this cannot be undone.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/work-log-categories/${category.categoryId}`);
      if (editingId === category.categoryId) resetForm();
      await loadCategories();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete this category.', 'error');
    }
  };

  return (
    <DashboardLayout title="Work Log Categories">
      <a href="/admin/my-work-log" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        My Work Log
      </a>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Work Log Categories</h1>
        <p className="mt-1 text-sm text-gray-600">
          These appear in the Category dropdown when staff log their daily work. A category with &ldquo;Pin last&rdquo;
          checked always sorts to the end of the list, no matter its name.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <form onSubmit={handleSubmit} className="lg:col-span-1 h-fit rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-900">{editingId ? 'Edit category' : 'Add category'}</h2>
          <div className="mt-4">
            <label className="mb-1 block text-sm font-semibold text-gray-700">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
              placeholder="e.g. Schedule"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={pinLast} onChange={(e) => setPinLast(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
            Always show this category last (e.g. &ldquo;Others&rdquo;)
          </label>
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Add category'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-900">Current categories</h2>
          <div className="mt-4 space-y-2">
            {loading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : categories.length === 0 ? (
              <p className="text-sm text-gray-400">No categories yet.</p>
            ) : (
              categories.map((category) => (
                <div key={category.categoryId} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{category.name}</span>
                    {category.pinLast && (
                      <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">PINNED LAST</span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" onClick={() => startEdit(category)} className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(category)} className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-100">
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
